import React, { memo, useRef, useReducer, useCallback, useMemo, useEffect } from 'react';

import { createUseStyles, Text, Modal } from '@Sage/hoi-poi-ui';
import { isEqual } from '@Sage/ssm-utils';

import ForceForm, { validate } from 'components/ForceForm';
import {
    getLicenses,
    getBillingPlatform,
    getTemplate,
    createImplementation,
    getImplementationHandler,
    getPartners,
    getResponsiblePartners,
} from 'lib/services/implementations';
import { useWeb5Context } from 'lib/Web5Context';
import { successToast, errorToast } from 'utils/toast';

import { FORM_SCHEMA } from './constants';
import styles from './styles/styles';
const useStyles = createUseStyles(styles, 'implementation');

const initialState = {
    originalData: {},
    data: {},
    errors: {},
    licenseOptions: [],
    partnerOptions: [],
    templateOptions: [],
    licenseEditionOptions: [],
    isLoading: false,
    isClosing: false,
    confirmClose: false,
    confirmSave: false,
    errorToast: '',
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...initialState,
            };
        case 'setLicenseOptions': // set lists
            return {
                ...state,
                licenseOptions: action.options,
                responsiblePartnersOptions: action.responsiblePartnersOptions,
            };
        case 'setPartnerOptions':
            return { ...state, partnerOptions: action.options };
        case 'setTemplateOptions':
            return { ...state, templateOptions: action.options };
        case 'setLicenseEditionOptions':
            return { ...state, licenseEditionOptions: action.options };
        case 'changeData':
            return { ...state, data: action.data };
        case 'setErrors': // form validation KO
            return { ...state, errors: action.errors };

        case 'setConfirmClose': // modal to confirm close
            return { ...state, confirmClose: true };
        case 'setConfirmSave': // modal to confirm save
            return { ...state, confirmSave: true };
        case 'closeConfirm': // close modals to confirm
            return { ...state, confirmClose: false, confirmSave: false };
        case 'isLoading':
            return { ...state, isLoading: action.value };
        default:
            return;
    }
}

const ImplementationForm = memo(({ isOpen, setIsOpen, onClose, switchModals }) => {
    const { t } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);
    const classes = useStyles();
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            dispatch({ type: 'init' });
            Promise.all([getLicenses(), getResponsiblePartners()])
                .then(([{ data }, { data: responsiblePartnersOptions }]) => {
                    const licensesOptions = data
                        .filter((el) => el.creation)
                        .map((el) => ({
                            label: el.licenseCode,
                            value: el.id,
                        }));

                    dispatch({
                        type: 'setLicenseOptions',
                        options: licensesOptions,
                        responsiblePartnersOptions,
                    });
                    return getPartners();
                })
                .then(({ data }) => {
                    const partnersOptions = data.map((el) => ({
                        label: el.name,
                        value: el.id,
                    }));
                    dispatch({ type: 'setPartnerOptions', options: partnersOptions });
                })
                .catch();
        }
    }, []);

    const modifyFieldProps = useCallback(
        (field, schema) => {
            let newSchema = schema;
            let newField = { ...field };

            // SHOW/HIDE FIELDS
            //    if free
            if (state.data?.isFree) {
                newSchema.layout.composition[0].composition[2].isHidden = false;
                newSchema.layout.composition[0].composition[3].isHidden = false;
                newSchema.layout.composition[0].composition[7].isHidden = true;
                newSchema.layout.composition[0].composition[9].isHidden = true;
            } else {
                newSchema.layout.composition[0].composition[2].isHidden = true;
                newSchema.layout.composition[0].composition[3].isHidden = true;
                newSchema.layout.composition[0].composition[7].isHidden = false;
                newSchema.layout.composition[0].composition[9].isHidden = false;
            }

            //    Partner settings
            newSchema.layout.composition[2].composition[3].isHidden = !state.data?.isPartner;

            // Backend needs to add the fields first in order to being able to use it on our cruds
            // else {
            //     newSchema.layout.composition[2].composition[1].isHidden = false;
            // }

            // if (state.data?.customerOfPartner) {
            //     newSchema.layout.composition[2].composition[2].isHidden = false;
            // } else {
            //     newSchema.layout.composition[2].composition[2].isHidden = true;
            // }

            // SET LIST OPTIONS
            if (state.licenseOptions) {
                newSchema.layout.composition[0].composition[5].options = state.licenseOptions;
            }
            if (state.partnerOptions) {
                newSchema.layout.composition[2].composition[2].options = state.partnerOptions;
            }
            if (state.templateOptions) {
                newSchema.layout.composition[0].composition[6].options = state.templateOptions;
            }
            if (state.licenseEditionOptions) {
                newSchema.layout.composition[0].composition[7].options =
                    state.licenseEditionOptions;
            }
            switch (field.schemaId || field.id) {
                case 'responsiblepartner':
                    newField = {
                        ...newField,
                        options: state?.responsiblePartnersOptions,
                    };
                    break;
                default:
                    break;
            }

            return newField;
        },
        [
            state.data?.isFree,
            state.data?.isPartner,
            state.licenseOptions,
            state.partnerOptions,
            state.templateOptions,
            state.licenseEditionOptions,
            state?.responsiblePartnersOptions,
        ],
    );

    const onChange = useCallback((data, field) => {
        let finalData;
        switch (field) {
            case 'implementationType':
                let implementationType = data?.implementationType?.value;
                finalData = {
                    ...data,
                    templateSelection: null,
                    licenseEdition: null,
                };
                if (implementationType) {
                    // Getting lists
                    getBillingPlatform(implementationType)
                        .then(({ data }) => {
                            let billingOptions = data.map((el) => ({
                                label: el.description,
                                value: el.id,
                                billingPlatform: el.billingPlatform, // "recurly_es"
                                platformPlanCode: el.platformPlanCode, // "sta2112"
                            }));
                            dispatch({ type: 'setLicenseEditionOptions', options: billingOptions });
                            return getTemplate(implementationType);
                        })
                        .then(({ data }) => {
                            let templateOptions = data.map((el) => ({
                                label: el.description,
                                value: el.id,
                            }));
                            dispatch({ type: 'setTemplateOptions', options: templateOptions });
                        })
                        .catch((err) => console.error(err));
                } else {
                    dispatch({
                        type: 'setLicenseEditionOptions',
                        options: [],
                    });
                    dispatch({
                        type: 'setTemplateOptions',
                        options: [],
                    });
                }
                dispatch({ type: 'changeData', data: finalData });
                break;
            case 'isFree':
                finalData = {
                    ...data,
                    userRequested: null,
                    isFreeReason: null,
                    licenseEdition: null,
                    totalUsersPurchased: null,
                };
                dispatch({ type: 'changeData', data: finalData });
                break;
            case 'isPartner':
                finalData = {
                    ...data,
                    customerOfPartner: false,
                    selectPartner: null,
                };
                dispatch({ type: 'changeData', data: finalData });
                break;
            default:
                dispatch({ type: 'changeData', data });
                break;
        }
    }, []);

    const customValidations = useMemo(() => {
        let validated = true;
        let errors = {};

        // URL
        if (state.data?.implementationUrl) {
            try {
                new URL(state.data?.implementationUrl);
            } catch {
                validated = false;
                errors = {
                    ...errors,
                    implementationUrl: {
                        message: 'cfm_label_url_invalid',
                    },
                };
            }
        }
        return { isValid: validated, errors };
    }, [state.data]);

    const onValidateData = useCallback(() => {
        let validated = customValidations.isValid;
        let totalErrors = customValidations.errors;

        validate(state.data, FORM_SCHEMA).then(({ isValid, errors, cleanData }) => {
            validated = validated && isValid;
            totalErrors = { ...totalErrors, ...errors };
            if (!validated) dispatch({ type: 'setErrors', errors: totalErrors });
            else dispatch({ type: 'setConfirmSave' });
        });
    }, [customValidations, state.data]);

    const getImplID = useCallback(
        (tag) => {
            setTimeout(() => {
                getImplementationHandler(tag)
                    .then((res) => {
                        switch (res.data.status) {
                            case 0:
                                getImplID(tag);
                                break;
                            case 1:
                                let newImplementationName = res.data.fullName;
                                if (res.data.status === 1) {
                                    // created correctly
                                    successToast({
                                        t,
                                        title: t('cfm_label_success_action'),
                                    });
                                    dispatch({ type: 'isLoading', value: false });
                                    onClose(newImplementationName.split('-')[0]);
                                    break;
                                } else {
                                    // not created correctly
                                    dispatch({
                                        type: 'isLoading',
                                        value: false,
                                    });
                                    errorToast({
                                        t,
                                        title: t('cfm_label_error_action'),
                                    });
                                    break;
                                }
                            case 2:
                                dispatch({
                                    type: 'isLoading',
                                    value: false,
                                });
                                errorToast({
                                    t,
                                    title: t('cfm_label_error_action'),
                                });
                                break;
                            default:
                                break;
                        }
                    })
                    .catch((err) => {
                        dispatch({
                            type: 'isLoading',
                            value: false,
                        });
                        errorToast({
                            t,
                            title: t('cfm_label_error_action'),
                        });
                    });
            }, 3000);
        },
        [onClose, t],
    );

    const onSaveData = useCallback(() => {
        dispatch({ type: 'isLoading', value: true });

        let chars = '0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        let passwordLength = 12;
        let password = '';
        const extIds = [];

        if (state.data?.extIds?.length) {
            extIds.push(state.data?.extIds);
        }
        const isSage = state.data.implementationType?.label?.includes('sage') && 'sage';
        const listMainPartners =
            state.data?.responsiblepartner?.length &&
            state.data?.responsiblepartner?.map(({ value }) => value).filter(Boolean);

        for (let i = 0; i <= passwordLength; i++) {
            let randomNumber = Math.floor(Math.random() * chars.length);
            password += chars.substring(randomNumber, randomNumber + 1);
        }

        let newData = {
            templateId: state.data?.templateSelection?.value,
            implementationName: `${state.data?.implementationName}`,
            isPartner: state.data.isPartner || false,
            isFree: state.data.isFree || false,
            countryCode: `${state.data.country.id}`,
            timeZoneCode: `${state.data.defaultTimezone.id}`,
            implementationFreeOwner: state.data.isFree ? `${state.data.userRequested}` : '',
            implementationReason: state.data.isFree
                ? `${state.data.isFreeReason?.value || ''}`
                : '',
            billingPlatform: state.data.isFree ? 'free' : state.data.licenseEdition.billingPlatform,
            subscriptionMode: state.data.isFree ? 'free' : 'paid',
            licenseEdition: state.data.implementationType?.label,
            billingPlatformPlanCode: state.data.isFree
                ? 'free'
                : state.data.licenseEdition?.platformPlanCode,
            totalUsersPurchased: state.data?.totalUsersPurchased,
            data: {
                Lastname: state.data.Lastname,
                Name: state.data.Name,
                IsAdmin: true,
                Email: state.data.Email,
                Phone: state.data.Phone,
                Password: password,
            },
            implementationUrl: state?.data?.implementationUrl,
            type: isSage || null,
            createApiKey: isSage ? true : false,
            listMainPartners,
            extIds,
        };

        createImplementation(newData)
            .then((res) => {
                return getImplID(res.data.tag);
            })
            .catch((err) => {
                dispatch({ type: 'closeConfirm' });
                dispatch({ type: 'isLoading', value: false });
                errorToast({
                    t,
                    title: err?.response?.data?.message || 'error',
                });
            });
    }, [getImplID, state?.data, t]);

    const onCancel = useCallback(() => {
        const noChanges = isEqual(state?.originalData, state?.data);
        if (noChanges) {
            switchModals && switchModals();
            setIsOpen && setIsOpen(false);
        } else {
            dispatch({ type: 'setConfirmClose' });
        }
    }, [setIsOpen, state?.data, state?.originalData, switchModals]);

    const onDiscardChanges = useCallback(() => {
        switchModals && switchModals();
        setIsOpen && setIsOpen(false);
    }, [setIsOpen, switchModals]);

    return (
        <>
            <Modal
                title={t('cfm_label_modal_create_implementation')}
                isOpen={isOpen}
                shouldCloseOnOverlayClick={false}
                onRequestClose={onCancel}
                onConfirm={onValidateData}
                confirmText={t('cfm_action_save')}
                onCancel={onCancel}
                cancelText={t('cfm_action_cancel')}
                isConfirmLoading={state.isLoading}
            >
                <ForceForm
                    className={classes.form}
                    mode="create"
                    modifyFieldProps={modifyFieldProps}
                    onChange={onChange}
                    values={state.data}
                    errors={state.errors}
                    schema={FORM_SCHEMA}
                />
            </Modal>
            {/* Modal confirm saving */}
            <Modal
                className={classes.modalConfirm}
                isOpen={state.confirmSave}
                shouldCloseOnOverlayClick={false}
                useCornerClose={false}
                cancelText={t('cfm_action_close')}
                onCancel={() => dispatch({ type: 'closeConfirm' })}
                confirmText={t('cfm_action_accept')}
                onConfirm={onSaveData}
                isConfirmLoading={state.isLoading}
                size="small"
            >
                <Text>{t('cfm_label_implementation_creation_confirmation')}</Text>
            </Modal>

            {/* Modal confirm close */}
            <Modal
                className={classes.modalConfirm}
                isOpen={state.confirmClose}
                shouldCloseOnOverlayClick={false}
                useCornerClose={false}
                cancelText={t('action_back')}
                onCancel={() => dispatch({ type: 'closeConfirm' })}
                confirmText={t('cfm_action_accept')}
                onConfirm={onDiscardChanges}
                size="small"
            >
                <Text>{t('cfm_label_implementation_creation_cancelation')}</Text>
            </Modal>
        </>
    );
});

export default ImplementationForm;
